import { Link } from "react-router-dom";
import { useSetAtom } from "jotai";
import { ROUTES } from "@routing/routes";
import { Icon } from "@common/index";
import { SupportLinkedText } from "@customer/_components/SupportLinkedText";
import { isShipToSwitcherOpenAtom } from "@/store/shipToAtoms";
import {
    RestrictionReason,
    type Product,
    type ProductRestriction,
} from "@models/product";
import { ProductGallery } from "../ProductGallery";
import { InfoAccordion } from "../InfoAccordion";
import { restrictionMessage } from "../_helpers/restrictionMessage";
import { ProductCoaDownload } from "./ProductCoaDownload";

interface ProductNotAvailableProps {
    product: Product;
    restriction: ProductRestriction;
}

// The product detail page for a product that EXISTS but cannot ship to the shopper's
// destination (#228 / E4-S12). The API answers 200 for it on purpose, so a direct link — a
// bookmark, a shared URL, a search result from before the location changed — must land here and
// not on a not-found page.
//
// What is deliberately absent:
//   every price      — the API zeroes price, msrp, pricePerPiece and each variant's, so a
//                      rendered "$0.00" would read as free. The lockup, the piece line and the
//                      variant chips (which quote their own price) are all withheld.
//   the quantity stepper and Add to Cart — WITHHELD, not disabled. `vendors` is empty, so
//                      there is no offering to buy; a greyed-out button invites a shopper to
//                      keep trying something that can never succeed.
//   the stock line   — isAvailable is false for the restriction, not for the shelf, so "Out of
//                      stock" would be a lie about a product that may be in stock everywhere
//                      else.
//
// What stays: enough identity to recognise the product (gallery, name, brand, description,
// details), the reason, and the ways out. Keyed on the block's `reason` (#410 / #417): a shopper
// with NO destination (seated on no location) is told so in account terms — there is no "this
// location" to name and no location to switch to, so the switcher button is withheld for them —
// the category link is the only way out — rather than opening onto an empty picker.
export const ProductNotAvailable = ({
    product,
    restriction,
}: ProductNotAvailableProps) => {
    const setSwitcherOpen = useSetAtom(isShipToSwitcherOpenAtom);
    const isNoDestination =
        restriction.reason === RestrictionReason.NoDestination;
    const canChangeLocation = !isNoDestination;

    const descriptionAccordion = (
        <InfoAccordion title="Description" defaultOpen>
            <p>{product.description}</p>
        </InfoAccordion>
    );

    return (
        <div data-testid="product-not-available">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-8 mt-2">
                <div className="flex flex-col gap-6">
                    <ProductGallery product={product} />
                </div>

                <div className="flex flex-col gap-4">
                    <header className="space-y-1">
                        <h1 className="text-2xl md:text-3xl font-bold text-neutral">
                            {product.name} by {product.brand}
                        </h1>
                    </header>

                    {/* Explicit text-*-content: the light theme sets --color-base-content
                        to white (see the CLAUDE.md theme quirk), so an alert must never
                        inherit a base-content foreground.

                        A destination refusal is a compliance block: warning colour, block
                        glyph, role="alert". No destination (#417) is not a refusal but an
                        account state, and nothing about it may read as an error (see
                        noLocationCopy.ts) — so it takes NoLocationNote's shape: info colour,
                        info glyph, role="status". */}
                    <div
                        role={isNoDestination ? "status" : "alert"}
                        data-testid="restriction-notice"
                        // Support-facing only — never rendered as copy (Lead ruling 7).
                        // restrictionId is null for a NoDestination block and for a class
                        // this build cannot name (#411); the name is always set.
                        data-restriction-id={restriction.restrictionId ?? undefined}
                        data-legal-class={restriction.legalClassName}
                        className={
                            isNoDestination
                                ? "alert alert-info text-info-content"
                                : "alert alert-warning text-warning-content"
                        }
                    >
                        <Icon name={isNoDestination ? "info" : "block"} filled />
                        <div>
                            <h2 className="font-bold">
                                {isNoDestination
                                    ? "Not available for your account"
                                    : "Not available at this location"}
                            </h2>
                            <p>
                                <SupportLinkedText
                                    text={restrictionMessage(restriction)}
                                />
                            </p>
                        </div>
                    </div>

                    <div className="flex flex-col sm:flex-row gap-2">
                        {canChangeLocation && (
                            <button
                                type="button"
                                onClick={() => setSwitcherOpen(true)}
                                className="btn btn-warning text-neutral font-bold"
                            >
                                Change shipping location
                            </button>
                        )}
                        <Link
                            to={ROUTES.BROWSE_CATEGORY(product.categoryId)}
                            className="btn btn-outline text-neutral border-neutral hover:bg-base-200"
                        >
                            Browse {product.categoryName}
                        </Link>
                    </div>

                    <div className="md:hidden">{descriptionAccordion}</div>

                    <InfoAccordion
                        title="Product Details"
                        defaultOpen
                        testId="product-details-section"
                    >
                        <dl className="grid grid-cols-[6rem_1fr] gap-y-2 gap-x-3">
                            <dt className="text-neutral/70">Brand:</dt>
                            <dd className="font-semibold">{product.brand}</dd>
                            <dt className="text-neutral/70">UPC:</dt>
                            <dd className="font-semibold">{product.upc}</dd>
                        </dl>
                        {/* No Vendor row: the API empties `vendors` for a restricted product,
                            so there is no vendor to name — an em-dash there would read as a
                            data gap rather than the deliberate withholding it is. */}
                        {/* I3: a product the destination cannot receive still
                            offers its COA (Lead call Q-D, #476); the API serves
                            it without a ship-to check. */}
                        {product.coa && (
                            <ProductCoaDownload
                                productId={product.id}
                                coa={product.coa}
                            />
                        )}
                    </InfoAccordion>
                </div>
            </div>

            <div className="hidden md:block mt-6">{descriptionAccordion}</div>
        </div>
    );
};
